import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getEvent } from "../../api/event";
import { getEventImage } from "../../utils/eventImageUtils";
import { getEventIcon } from "../../utils/eventIconUtils";
import { capitalizeFirstLetter } from "../../utils/stringUtils";
import lineEndCircle from "../../assets/line_end_circle.svg";
import { useToast } from "../../context/ToastContext";
import {
  Home, Coffee, PartyPopper, Truck, Camera, Users as UsersIcon, Shield, Plus,
  Disc, Mic2, Music, Speaker, Smile, Keyboard, User,
  Zap, Flag, Video, Star, ListPlus, Users2, Info,
  Calendar, Clock, MapPin, Users, ArrowLeft, X, ChevronRight, ChevronLeft,
  CalendarCheck, Handshake, PiggyBank, Building2, Clapperboard,
  UserCheck, UtensilsCrossed, FileCheck2, BedDouble, Plane,
  ClipboardList, BadgeCheck, Rocket, Megaphone, Palette, Store,
  Package, UserPlus, CalendarDays, UserCog, ClipboardCheck, BarChart2,
  TrendingUp, MapPinned, LayoutGrid, Navigation, Crown, Boxes, Cpu,
  CalendarRange, Ticket, Lightbulb, Volume2, Mail, GraduationCap,
  BookOpen, Mic, Trophy, ShieldCheck, PersonStanding, Piano, Search, Sparkles
} from "lucide-react";

const ICON_MAP = {
  // General
  "CalendarCheck": CalendarCheck, "Handshake": Handshake, "PiggyBank": PiggyBank,
  "Clock": Clock, "Building2": Building2, "Truck": Truck, "Clapperboard": Clapperboard,
  "MapPin": MapPin, "Users2": Users2, "UserCheck": UserCheck, "Star": Star,
  "Mic2": Mic2, "PartyPopper": PartyPopper, "UtensilsCrossed": UtensilsCrossed,
  "Shield": Shield, "FileCheck2": FileCheck2,
  // Planner
  "BedDouble": BedDouble, "Plane": Plane, "ClipboardList": ClipboardList,
  "Speaker": Speaker, "BadgeCheck": BadgeCheck, "Flag": Flag, "Rocket": Rocket,
  "Megaphone": Megaphone, "Users": UsersIcon, "Palette": Palette, "Store": Store,
  "Package": Package, "UserPlus": UserPlus, "CalendarDays": CalendarDays,
  "UserCog": UserCog, "ClipboardCheck": ClipboardCheck, "BarChart2": BarChart2,
  "TrendingUp": TrendingUp, "MapPinned": MapPinned, "LayoutGrid": LayoutGrid,
  "Navigation": Navigation, "Crown": Crown, "Boxes": Boxes, "Cpu": Cpu,
  "Music": Music, "CalendarRange": CalendarRange, "Ticket": Ticket,
  "Lightbulb": Lightbulb, "Volume2": Volume2, "Mail": Mail, "Camera": Camera,
  "GraduationCap": GraduationCap, "BookOpen": BookOpen, "Mic": Mic,
  "Trophy": Trophy, "ShieldCheck": ShieldCheck,
  // Performer
  "Disc": Disc, "Smile": Smile, "Piano": Piano, "PersonStanding": PersonStanding,
  // Crew
  "Zap": Zap, "ListPlus": ListPlus, "Video": Video,
  
  // Taxonomy & Sub-Expertises
  "End-to-End Event Planning": ClipboardList, "Vendor Coordination": UsersIcon,
  "Budget Planning": PiggyBank, "Timeline Planning": Clock, "Venue Management": Home,
  "Logistics Management": Truck, "Production Management": Zap, "On-ground Operations": MapPin,
  "Volunteer Coordination": Users2, "Guest Management": UserCheck, "Hospitality Management": Star,
  "Stage & AV Coordination": Mic2, "Décor Coordination": PartyPopper, "Catering Coordination": UtensilsCrossed,
  "Security Coordination": Shield, "Permissions & Compliance": FileCheck2, "Accommodation Management": BedDouble,
  "Travel Coordination": Plane, "Registration Management": ClipboardCheck, "VIP Protocol Coordination": Crown,
  "Branding & Signage": Flag,
  "3 Piece Band": Music, "4 Piece Band": Music, "5+ Piece Band": Music, "Wedding Band": Music, "Corporate Band": Music, "Party Band": Music,
  "Bollywood": Mic2, "Retro": Disc, "Punjabi": Music, "Sufi": Sparkles, "Ghazal": Mic, "Classical": Piano, "Semi - Classical": Piano, "Folk": Music, "Indie": Music, "Pop": Music, "Rock": Music, "Jazz": Music, "Acoustic": Volume2, "EDM": Disc, "Rap": Mic2, "Devotional": Sparkles, "Qawwali": Mic,
  "Commercial": Disc, "House": Disc, "Techno": Disc, "Trance": Disc, "Hip-Hop": Disc, "Wedding DJ": Disc, "Club DJ": Disc, "Corporate DJ": Disc,
  "Solo": PersonStanding, "Duo": UsersIcon, "Trio": Users2, "Dance Group": Users2, "Vocalist": Mic2, "Bhangra": UserCheck, "Belly Dance": Sparkles, "Salsa": Sparkles, "Kathak": PersonStanding, "Bharatnatyam": PersonStanding, "Freestyle": Sparkles, "LED Dance": Sparkles,
  "Wedding Anchor": Speaker, "Corporate Emcee": Speaker, "Conference Host": Speaker, "Award Show Host": Trophy, "Product Launch Host": Rocket, "College Fest Host": PartyPopper, "Concert Host": Mic2, "Sports Host": Trophy, "Government Event Anchor": Shield, "TV Host": Video, "Emcee / Anchor": Speaker,
  "Illusionist": Sparkles, "Mind Reader": Lightbulb, "Street Magic": Sparkles, "Close-up Magic": Sparkles, "Stage Magic": Sparkles, "Kids Magic": Smile, "Corporate Magician": Sparkles, "Trade Show Magician": Sparkles, "Fire Magic": Zap, "Digital / iPad Magic": Cpu, "Escape Artist": Sparkles, "Hypnotist": Sparkles, "Magician": Sparkles, "Mentalist": Lightbulb,
  "Voice Over Artist": Mic, "Celebrity Impressionist": Smile, "Mimicry Artist": Smile,
  "Kavi": BookOpen, "Shayari": BookOpen, "Standup Poet": BookOpen, "Mushaira": BookOpen, "Poet / Shayari Artist": BookOpen,
  "Ventriloquist": Smile, "Puppeteer": Smile, "Speed Painter": Palette, "Sand Artist": Palette, "Live Painter": Palette, "Caricature Artist": Palette,
  "Balloon Artist": PartyPopper, "Tattoo Artist": Palette, "Face Painter": Palette, "Mascot": Smile, "Kids Entertainer": Smile,
  "Stilt Walker": PersonStanding, "Fire Performer": Zap, "Aerialist": Sparkles, "Juggler": Sparkles, "Unicyclist": Sparkles, "Hula Hoop": Sparkles, "Contortionist": PersonStanding, "Living Statue": PersonStanding, "Bubble Artist": Sparkles, "Shadow Act": Sparkles, "Circus & Specialty Acts": Sparkles,
  "Celebrity Lookalike": Crown, "Event Model": Crown, "Clean Comedy": Smile, "Stand-up Comedy": Smile, "Improv": Smile,

  // Legacy
  "Venue": Home, "Catering": Coffee, "Decor": PartyPopper, "Logistics": Truck,
  "Photography": Camera, "Coordination": UsersIcon, "Security": Shield,
  "DJ": Disc, "Singer": Mic2, "Live Band": Music, "Host/MC": Speaker,
  "Comedian": Smile, "Instrumentalist": Keyboard, "Dancer": User,
  "Runners": Truck, "Shadow": Zap, "Usher": Flag, "Technical": Video,
  "Hospitality": Star, "Promoter": ListPlus, "Volunteers": Users2,
  "Custom": Plus,
};

const getIconForRequirement = (srv) => {
  if (!srv) return Sparkles;
  if (typeof srv === 'object' && srv?.isSuggestion) return Plus;
  const name = typeof srv === 'string' ? srv : (srv?.name || srv?.title || srv?.label || "");
  const iconStr = typeof srv === 'object' && srv?.icon ? srv.icon : null;

  if (name && ICON_MAP[name]) return ICON_MAP[name];
  if (iconStr && ICON_MAP[iconStr]) return ICON_MAP[iconStr];

  if (name && typeof name === 'string') {
    const match = Object.keys(ICON_MAP).find(k => k.toLowerCase() === name.toLowerCase());
    if (match) return ICON_MAP[match];
  }
  if (iconStr && typeof iconStr === 'string') {
    const match = Object.keys(ICON_MAP).find(k => k.toLowerCase() === iconStr.toLowerCase());
    if (match) return ICON_MAP[match];
  }
  const isDbObject = typeof srv === 'object' && srv !== null && srv?._id && /^[0-9a-fA-F]{24}$/.test(String(srv._id));
  if (name && typeof name === 'string' && !/^[0-9a-fA-F]{24}$/.test(name)) {
    if (!isDbObject) {
      return Plus;
    }
  }
  return Sparkles;
};

export default function BookingEventDetails({ eventId, onEventLoaded }) {
  const { showInfo } = useToast();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  const scrollContainerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = useCallback(() => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 2);
      setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 4);
    }
  }, []);

  useEffect(() => {
    if (!eventId) return;
    (async () => {
      try {
        setLoading(true);
        const res = await getEvent(eventId);
        const evt = res?.data || res;
        setEvent(evt);
        if (onEventLoaded) onEventLoaded(evt);
      } catch (e) {
        console.error("Error loading event data", e);
      } finally {
        setLoading(false);
      }
    })();
  }, [eventId, onEventLoaded]);

  useEffect(() => {
    checkScroll();
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener("scroll", checkScroll);
      window.addEventListener("resize", checkScroll);
      return () => {
        container.removeEventListener("scroll", checkScroll);
        window.removeEventListener("resize", checkScroll);
      };
    }
  }, [checkScroll, event]);
  const handleScroll = (direction) => {
    if (scrollContainerRef.current) {
      const cardWidth = 97; // 85px card width + 12px gap
      scrollContainerRef.current.scrollBy({
        left: direction === "right" ? cardWidth : -cardWidth,
        behavior: "smooth",
      });
    }
  };

  if (loading) {
    return <div className="p-10 text-center text-[#FF4C18] font-bold animate-pulse">Loading Event Details...</div>;
  }

  if (!event) {
    return (
      <div className="bg-white rounded-[24px] border border-gray-200 p-8 w-full text-center shadow-xs">
        <p className="text-gray-500 font-medium text-sm">Event details are unavailable for this record.</p>
      </div>
    );
  }

  const isPlanner = event?.roleType === "planner";
  const isCrewOrPerformer = event?.roleType === "crew" || event?.roleType === "performer";

  const eventTypeName = event?.eventTypeText || (typeof event?.eventType === 'object' && event?.eventType !== null ? event.eventType.name : event?.eventType);
  const EventIcon = getEventIcon(eventTypeName);
  const locationName = Array.isArray(event?.locations) && event.locations.length > 0
    ? event.locations.map(l => typeof l === 'object' ? (l.city || l.name || '') : l).filter(Boolean).join(", ")
    : (typeof event?.location === 'object' && event?.location !== null ? event.location.name : event?.location);

  const descText = event?.additionalInfo || "No additional information provided.";
  const showReadMore = descText.length > 220;
  const displayText = expanded ? descText : (showReadMore ? `${descText.slice(0, 220)}...` : descText);

  return (
    <div className="bg-white rounded-[20px] sm:rounded-[24px] border border-gray-155 shadow-[0_2px_12px_rgba(0,0,0,0.03)] p-3.5 sm:p-5 md:p-6 w-full h-full flex flex-col justify-between">
        {/* Top Split Layout: Image on left, Content on right */}
        <div className="flex flex-col md:flex-row gap-4 sm:gap-6 mb-4">
          <div className="w-full md:w-[320px] h-[220px] rounded-[24px] overflow-hidden shrink-0">
            <img
              src={event?.image || getEventImage(eventTypeName)}
              alt="Event cover"
              className="w-full h-full object-cover"
            />
          </div>
          
          <div className="flex-1 flex flex-col justify-start">
            <h2 className="text-2xl font-bold text-gray-900 mb-2 leading-tight">
              {capitalizeFirstLetter(event?.eventName) || "Event Details"}
            </h2>
            <p className="text-gray-600 font-medium text-sm leading-relaxed mb-4">
              {displayText}
              {showReadMore && (
                <span
                  onClick={() => setExpanded(!expanded)}
                  className="text-[#FF4D12] font-bold ml-1 cursor-pointer hover:underline"
                >
                  {expanded ? "Read Less" : "Read More."}
                </span>
              )}
            </p>
          </div>
        </div>

        {/* Metadata Outline Icons Row */}
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-[14px] text-gray-700 font-medium mb-2">
          <div className="flex items-center gap-2">
            <EventIcon className="w-5 h-5 text-[#FF4D12]" />
            <span>{eventTypeName}</span>
          </div>
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-[#FF4D12]" />
            <span>{event?.startDate ? new Date(event?.startDate).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" }) : "Date TBD"}</span>
          </div>
          {event?.endDate && event?.endDate !== event?.startDate && (
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#FF4D12]" />
              <span>{new Date(event?.endDate).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" })}</span>
            </div>
          )}
          {isCrewOrPerformer && (
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-[#FF4D12]" />
              <span>{event?.durationHours ? `${event?.durationHours} hours` : "Duration TBD"}</span>
            </div>
          )}
        </div>

        {/* Pax count row */}
        {isPlanner && (
          <div className="flex items-center gap-2 text-[14px] text-gray-700 font-medium mb-2">
            <User className="w-5 h-5 text-[#FF4D12]" />
            <span>{event?.guests ? `${event?.guests} pax` : "Pax TBD"}</span>
          </div>
        )}

        {/* Full Location(s) Row with lineEndCircle icon */}
        <div className="flex items-start gap-2.5 text-[14px] text-gray-800 font-medium mb-2">
          <img src={lineEndCircle} alt="Location Icon" className="w-[12px] h-[20px] mt-0.5 shrink-0" />
          <span className="leading-snug">
            {locationName || "Location not provided"}
          </span>
        </div>

      {/* Service Requirements Slider */}
      {((event?.servicesRequired && event.servicesRequired.length > 0) || (event?.subServicesRequired && event.subServicesRequired.length > 0)) && (
        <div className="flex items-center w-full gap-2 mt-2 py-1">
          {/* Left Chevron Arrow - Desktop Only */}
          {canScrollLeft && (
            <button
              type="button"
              onClick={() => handleScroll("left")}
              className="hidden md:flex items-center justify-center shrink-0 text-[#FF4C18] hover:scale-110 transition-transform p-1"
              title="Previous"
            >
              <ChevronLeft className="w-7 h-7 stroke-[3.5px]" />
            </button>
          )}

          {/* Scrollable Container */}
          <div
            ref={scrollContainerRef}
            className="flex flex-nowrap overflow-x-auto hide-scrollbar [&::-webkit-scrollbar]:hidden gap-3 pb-2 py-1 flex-1 min-w-0"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          >
            {[...(event.servicesRequired || []), ...(event.subServicesRequired || [])].map((req, i) => {
              const reqName = typeof req === 'string' ? req : (req?.name || req?.title || req?.label || "");
              if (!reqName || /^[0-9a-fA-F]{24}$/.test(reqName)) return null;
              const Icon = getIconForRequirement(req);
              const isSuggestion = typeof req === 'object' && req?.isSuggestion;
              return (
                <div
                  key={i}
                  onClick={isSuggestion ? () => showInfo("Validation is pending") : undefined}
                  className={`w-[85px] h-[85px] md:w-[95px] md:h-[95px] shrink-0 rounded-[20px] border flex flex-col items-center justify-center bg-white p-1.5 ${
                    isSuggestion 
                      ? "border-2 border-dotted border-orange-400 text-orange-500 animate-pulse cursor-pointer" 
                      : "border-[#FF4C18] text-[#FF4C18]"
                  }`}
                >
                  <Icon className={`w-[16px] h-[16px] md:w-[18px] md:h-[18px] mb-1 shrink-0 ${isSuggestion ? "text-orange-500 animate-pulse" : "text-[#FF4C18]"}`} />
                  <span className={`text-[8px] md:text-[9.5px] font-semibold uppercase tracking-wide text-center leading-tight px-0.5 line-clamp-2 ${isSuggestion ? "text-orange-500" : "text-[#FF4C18]"}`}>{reqName}</span>
                </div>
              );
            })}
          </div>

          {/* Right Chevron Arrow - Desktop Only */}
          {canScrollRight && (
            <button
              type="button"
              onClick={() => handleScroll("right")}
              className="hidden md:flex items-center justify-center shrink-0 text-[#FF4C18] hover:scale-110 transition-transform p-1"
              title="Next"
            >
              <ChevronRight className="w-7 h-7 stroke-[3.5px]" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
